//import React, { useState, useEffect, useCallback } from 'react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {  Download, Upload, FileSpreadsheet } from 'lucide-react';
import { Edit2, Trash2, Filter, ChevronDown, ChevronRight } from 'lucide-react';
import { fetchRevenueTargets, deleteRevenueTarget } from '../services/revTargetService';
import { fetchSubcounties } from '../services/subcountyService';
import { fetchUniqueStreams } from '../services/streamService';
import { getFinancialYearData } from '../utils/dateHelper';
import { formatWithCommas, stripCommas } from '../utils/fmtNoHelper';
import { exportToExcel } from '../utils/excelExportUtils'; // Use existing utility 
import { importRevTargetsFromExcel } from '../utils/revTargetExcelImport'; // New utility
import { useAuth } from '../context/useAuth';

const RevTargetList = ({ onEdit, isEditing }) => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subcounties, setSubcounties] = useState([]);
  const [streams, setStreams] = useState([]);
  const [expandedRows, setExpandedRows] = useState({}); // Track expanded monthly views
  const {user} = useAuth(); // Access user from context
  const fileInputRef = useRef(null);

  // Filter States
  const fyOptions = getFinancialYearData();
  const [selectedFY, setSelectedFY] = useState(fyOptions.current);
  //const [selectedSubCounty, setSelectedSubCounty] = useState('');
  const [selectedSubCounty, setSelectedSubCounty] = useState(user?.sub_county_id || '');
  const [selectedStream, setSelectedStream] = useState('');

  // Define month labels for the expansion grid
  const monthLabels = [
    { key: 'jul_target', label: 'Jul' }, { key: 'aug_target', label: 'Aug' },
    { key: 'sep_target', label: 'Sep' }, { key: 'oct_target', label: 'Oct' },
    { key: 'nov_target', label: 'Nov' }, { key: 'dec_target', label: 'Dec' },
    { key: 'jan_target', label: 'Jan' }, { key: 'feb_target', label: 'Feb' },
    { key: 'mar_target', label: 'Mar' }, { key: 'apr_target', label: 'Apr' },
    { key: 'may_target', label: 'May' }, { key: 'jun_target', label: 'Jun' }
  ];

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };
  
  const handleExport = () => {
      // Transform targets for a clean Excel layout 
      const exportData = targets.map(t => ({
        'Financial Year': t.financial_year,
        'Sub-County': t.sub_county_name,
        'Revenue Stream': t.rev_stream,
        'Annual Target': t.annual_target,
        'July': t.jul_target, 'August': t.aug_target, 'September': t.sep_target,
        'October': t.oct_target, 'November': t.nov_target, 'December': t.dec_target,
        'January': t.jan_target, 'February': t.feb_target, 'March': t.mar_target,
        'April': t.apr_target, 'May': t.may_target, 'June': t.jun_target
      }));

    exportToExcel(exportData, `Revenue_Targets_${selectedFY || 'All'}.xlsx`);
  };
  const handleImportClick = () => fileInputRef.current.click();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (window.confirm(`Importing targets from ${file.name}. This may overwrite existing data. Proceed?`)) {
      setLoading(true);
      try {
        const result = await importRevTargetsFromExcel(file);
        alert(`Successfully imported ${result.count} targets.`);
        loadTargets(); // Refresh list
      } catch (err) {
        alert("Error importing file. Please check the Excel format.");
      } finally {
        setLoading(false);
      }
    }
    e.target.value = ''; // Reset input
  };
  // Fetch metadata for dropdowns
 useEffect(() => {
  const loadMetadata = async () => {
    // 1. Safety check: Ensure functions are actually imported and defined
    if (typeof fetchSubcounties !== 'function' || typeof fetchUniqueStreams !== 'function') {
      console.error("Metadata functions are not defined. Check your imports.");
      return;
    }

    try {
      // 2. Execute parallel fetch
      const [scData, stData] = await Promise.all([
        fetchSubcounties(),
        fetchUniqueStreams()
      ]);

      // 3. Apply your restriction logic
      const allowedSubcounties = user?.sub_county_id 
        ? (scData || []).filter(sc => sc.id === user.sub_county_id)
        : (scData || []);

      setSubcounties(allowedSubcounties);
      setStreams(stData || []);
    } catch (err) {
      console.error("Metadata load error:", err);
    }
  };

  loadMetadata();
}, [user?.sub_county_id]); // Use optional chaining to prevent crashes if user is null

  const loadTargets = useCallback(async () => {
    setLoading(true);
    try {
      // Pass all filter parameters to the service
      const data = await fetchRevenueTargets(selectedFY, selectedSubCounty, selectedStream);
      setTargets(data);
    } catch (err) {
      console.error("Error loading targets:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedFY, selectedSubCounty, selectedStream]);

  useEffect(() => {
    loadTargets();
  }, [loadTargets]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this target?")) {
      try {
        await deleteRevenueTarget(id);
        alert("Target deleted successfully.");
        loadTargets();
      } catch (err) {
        alert("Error deleting target.");
      }
    }
  };
  const handleDownloadTemplate = () => {
    // Define the exact headers required by the import utility
    const templateData = [
      {
        'Sub-County ID': 1,
        'Stream': 'Advertisements',
        'FY': 'FY2025/2026',
        'Jul': 0, 'Aug': 0, 'Sep': 0, 'Oct': 0, 'Nov': 0, 'Dec': 0,
        'Jan': 0, 'Feb': 0, 'Mar': 0, 'Apr': 0, 'May': 0, 'Jun': 0
      }
    ];

    // Reuse your existing export utility to generate the template file
    exportToExcel(templateData, 'Revenue_Target_Template.xlsx');
  };

  return (
    <div className="space-y-4">
      {/* Expanded Filter Bar */}
      {/* Visual indicator that filters are locked */}
      <div className={`bg-white p-6 rounded-xl border border-gray-100 shadow-sm space-y-4 ${isEditing ? 'bg-gray-50 opacity-75' : ''}`}>
        <div className="flex items-center text-gray-700 font-bold border-b pb-2">
          <Filter size={20} className={`mr-2 ${isEditing ? 'text-gray-400' : 'text-blue-600'}`} />
          <span>{isEditing ? 'Filters Locked (Editing in Progress)' : 'Search & Filter Targets'}</span>
        </div>
        
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm space-y-6">
          {/* Row 1: Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* FY Filter */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase">Financial Year</label>
              <select
                disabled={isEditing} 
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedFY}
                onChange={(e) => setSelectedFY(e.target.value)}
              >
                <option value="">All Years</option>
                <option value={fyOptions.previous}>{fyOptions.previous}</option>
                <option value={fyOptions.current}>{fyOptions.current}</option>
                <option value={fyOptions.next}>{fyOptions.next}</option>
              </select>
            </div>

            {/* Sub-County Filter */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase">Sub-County</label>
              <select 
                disabled={isEditing} 
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedSubCounty}
                onChange={(e) => setSelectedSubCounty(e.target.value)}
              >
                {!user?.sub_county_id && <option value="">All Sub-Counties</option>}
                {subcounties.map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
              </select>
            </div>

            {/* Revenue Stream Filter */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase">Revenue Stream</label>
              <select 
                disabled={isEditing} 
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedStream}
                onChange={(e) => setSelectedStream(e.target.value)}
              >
                <option value="">All Streams</option>
                {streams.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {/* Row 2: Large Action Buttons */}
          <div className="flex flex-row flex-nowrap items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {/* Export Button */}
            <button 
              disabled={isEditing}
              onClick={handleExport}
              className="flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-all text-sm font-bold shadow-md whitespace-nowrap flex-shrink-0"
            >
              <Download size={20} />
              <span>Export To Excel</span>
            </button>
            
            {/* Import Button */}
            <button
              disabled={isEditing} 
              onClick={handleImportClick}
              className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-all text-sm font-bold shadow-md whitespace-nowrap flex-shrink-0"
            >
              <Upload size={20} />
              <span>Import From Excel</span>
            </button>
            {/* Download Template Button*/}
            <button 
              disabled={isEditing}
              onClick={handleDownloadTemplate}
              className="flex items-center space-x-2 bg-gray-600 text-white px-6 py-3 rounded-xl hover:bg-gray-700 transition-all text-sm font-bold shadow-md whitespace-nowrap flex-shrink-0"
              title="Download Import Template"
            >
              <FileSpreadsheet size={20} />
              <span>Download Template</span>
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-20 text-center text-gray-500 animate-pulse">Filtering targets...</div>
        ) : (
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-xs border-b">
              <tr>
                <th className="px-6 py-4 w-10"></th>
                <th className="px-6 py-4">Financial Year</th>
                <th className="px-6 py-4">Sub-County</th>
                <th className="px-6 py-4">Revenue Stream</th>
                <th className="px-6 py-4 text-right">Annual Total (KES)</th>
                <th className="px-6 py-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {targets.length > 0 ? (
                targets.map((t) => (
                  <React.Fragment key={t.id}>
                    <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => toggleRow(t.id)}>
                      <td className="px-4 py-4">
                        {expandedRows[t.id] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </td>
                      <td className="px-6 py-4 font-medium">{t.financial_year}</td>
                      <td className="px-6 py-4">{t.sub_county_name || 'N/A'}</td>
                      <td className="px-6 py-4 text-gray-600">{t.rev_stream}</td>
                      <td className="px-6 py-4 text-right font-bold text-blue-700">
                        {Number(t.annual_target).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-center space-x-2">
                          <button onClick={() => onEdit(t)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all"><Edit2 size={16} /></button>
                          <button onClick={() => handleDelete(t.id)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-all"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Monthly Breakdown Expansion */}
                    {expandedRows[t.id] && (
                      <tr className="bg-blue-50/30">
                        <td colSpan="6" className="px-10 py-6">
                          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                            {monthLabels.map(m => (
                              <div key={m.key} className="bg-white p-3 rounded-lg border border-blue-100 shadow-sm">
                                <p className="text-[10px] font-bold text-gray-400 uppercase">{m.label}</p>
                                <p className="text-sm font-bold text-blue-800">
                                  {/* Added comma separator here */}
                                  {formatWithCommas(Number(t[m.key] || 0).toString())}
                                </p>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="p-20 text-center text-gray-400 italic">No matching targets found.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RevTargetList;